import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiClient } from "@leap4/frontend";
import { campaignsAccessUrl, reportingBaseUrl } from "../../config/generals";
import { useAppSelector } from "../../store/hooks";
import { isCanceledError } from "../../utils/functions";
import { createRequestCancellation } from "../../utils/requestCancellation";

type ScopeStatus = "loading" | "ready" | "error";

export interface ICampaignEvent {
    event: string;
    display_label?: string;
    is_active?: boolean;
    is_attributed?: boolean;
    is_predicted?: boolean;
}

// Keep uploaded batch predictions in Reporting, where their settlement lag has context.
const PROJECTION_EXCLUDED_EVENTS = new Set(["verified_install_mb"]);
const EMPTY_EVENTS: ICampaignEvent[] = [];
const EMPTY_CAMPAIGNS: string[] = [];

interface ICampaignScope {
    campaigns: string[];
    /** Always one campaign id; empty until loaded or when the org has none. */
    campaign: string;
    setCampaign: (campaign: string) => void;
    status: ScopeStatus;
    events: ICampaignEvent[];
    configStatus: ScopeStatus;
    reloadConfig: () => void;
    projectionEvents: ICampaignEvent[];
    projectionEnabled: boolean;
}

const CampaignScopeContext = createContext<ICampaignScope | null>(null);

export const useCampaignScope = (): ICampaignScope => {
    const ctx = useContext(CampaignScopeContext);
    if (!ctx) throw new Error("useCampaignScope must be used inside CampaignScopeProvider");
    return ctx;
};

/** Campaign access is keyed on organization, so the list and the selection reset whenever the org changes. */
export const CampaignScopeProvider = ({ children }: { children: ReactNode }) => {
    const selectedOrganizationId = useAppSelector((state: any) => state.auth?.selectedOrganizationId);
    const organizationKey = String(selectedOrganizationId ?? "");
    const [access, setAccess] = useState<{ key: string; campaigns: string[]; status: ScopeStatus }>({ key: "", campaigns: [], status: "loading" });
    const [selection, setSelection] = useState<{ key: string; campaign: string }>({ key: "", campaign: "" });
    const status: ScopeStatus = access.key === organizationKey ? access.status : "loading";
    const campaigns = useMemo(
        () => (access.key === organizationKey && status === "ready" ? access.campaigns : EMPTY_CAMPAIGNS),
        [access, organizationKey, status],
    );
    const campaign = status === "ready"
        ? (selection.key === organizationKey && campaigns.includes(selection.campaign) ? selection.campaign : campaigns[0] ?? "")
        : "";
    const setCampaign = useCallback((next: string) => {
        if (access.key === organizationKey && access.campaigns.includes(next)) setSelection({ key: organizationKey, campaign: next });
    }, [access, organizationKey]);
    const configKey = JSON.stringify([organizationKey, campaign]);
    const [config, setConfig] = useState<{ key: string; status: ScopeStatus; events: ICampaignEvent[] }>({ key: "", status: "loading", events: [] });
    const [configReload, setConfigReload] = useState(0);
    const reloadConfig = useCallback(() => setConfigReload((n) => n + 1), []);
    const configStatus: ScopeStatus = config.key === configKey ? config.status : "loading";
    const events = configStatus === "ready" ? config.events : EMPTY_EVENTS;
    const projectionEvents = useMemo(() => events.filter((event) =>
        event.is_active !== false && event.is_predicted === true && !PROJECTION_EXCLUDED_EVENTS.has(event.event),
    ), [events]);
    const projectionEnabled = status === "ready" && configStatus === "ready" && projectionEvents.length > 0;

    useEffect(() => {
        const cancellation = createRequestCancellation();
        let active = true;
        setAccess({ key: organizationKey, campaigns: [], status: "loading" });
        (async () => {
            try {
                const response = await apiClient.get<{ campaignIds: string[] }>(campaignsAccessUrl, cancellation.config);
                if (!active) return;
                setAccess({ key: organizationKey, campaigns: response?.data?.campaignIds ?? [], status: "ready" });
            } catch (err) {
                if (!active || isCanceledError(err)) return;
                console.error("Failed to fetch accessible campaigns:", err);
                setAccess({ key: organizationKey, campaigns: [], status: "error" });
            }
        })();
        return () => { active = false; cancellation.cancel(); };
    }, [organizationKey]);

    useEffect(() => {
        if (!campaign) return undefined;
        const cancellation = createRequestCancellation();
        let active = true;
        setConfig({ key: configKey, status: "loading", events: [] });
        apiClient.get<{ events?: ICampaignEvent[] }>(
            `${reportingBaseUrl}/campaign-config?id_campaign=${encodeURIComponent(campaign)}`, cancellation.config,
        ).then((response) => {
            if (!active) return;
            const next = (response?.data?.events ?? []).map((event) => ({
                ...event, event: String(event.event ?? "").trim().toLowerCase(),
            })).filter((event) => event.event);
            setConfig({ key: configKey, status: "ready", events: next });
        }).catch((error) => {
            if (!active || isCanceledError(error)) return;
            setConfig({ key: configKey, status: "error", events: [] });
        });
        return () => { active = false; cancellation.cancel(); };
    }, [campaign, configKey, configReload]);

    const value = useMemo(() => ({ campaigns, campaign, setCampaign, status, events, configStatus, reloadConfig, projectionEvents, projectionEnabled }),
        [campaigns, campaign, setCampaign, status, events, configStatus, reloadConfig, projectionEvents, projectionEnabled]);

    return <CampaignScopeContext.Provider value={value}>{children}</CampaignScopeContext.Provider>;
};
